// @flow

import i18n from 'i18next';
import React, { type Node } from 'react';

import { useUniqueId } from '@tectonics/hooks/use-unique-id';
import { useUserInputAsTextField } from '@tectonics/prs/user-input-values';
import { useUserInputValidationError } from '@tectonics/prs/validation/use-user-input-validation-result';
import { type UserInputValidationError } from '@tectonics/prs/validation/types';
import { serverErrorMessage } from '@tectonics/prs/validation/server-error-message';
import { InputMask } from '@tectonics/components/input-mask';
import { useSubmitting } from '@tectonics/prs/submitting';

import { TextField } from '../../../../../atoms';
import { useFormHasBeenSent } from '@tectonics/prs/form-status';
import { ErrorLabel } from '../../../../../molecules/error-label';
import { useTranslation } from 'react-i18next';

type Props = {
  rootStyle?: {},
};

export function FieldCardExpiryDate({ rootStyle }: Props): Node {
  const inputId = useUniqueId();

  const [value, handleChange] = useUserInputAsTextField('cardExpiryDate');

  const formHasBeenSent = useFormHasBeenSent();
  const validationError = useUserInputValidationError('cardExpiryDate');
  const submitting = useSubmitting();

  return (
    <div style={rootStyle} data-test-name="card-expiry-date">
      <label htmlFor={inputId}>
        <LabelMessage />
      </label>

      <TextField
        as={InputMask}
        id={inputId}
        type="tel"
        mask={EXPIRY_DATE_MASK}
        value={value}
        onChange={handleChange}
        fashion={formHasBeenSent && validationError ? 'error' : null}
        readOnly={submitting}
        data-error={formHasBeenSent && validationError ? 'error' : null}
        data-secret
        name="card-expiry-date"
        placeholder={PlaceHolderMessage()}
      />

      {formHasBeenSent && validationError && (
        <ValidationMessage validationError={validationError} />
      )}
    </div>
  );
}

function LabelMessage(): string {
  const { t } = useTranslation('volcanes');

  return t('fieldCardExpiryDate');
}

function PlaceHolderMessage(): string {
  const { t } = useTranslation('volcanes');

  return t('placeholderCardExpiryDate');
}

type ValidationMessageProps = {|
  validationError: UserInputValidationError,
|};

const messages = (validationError: UserInputValidationError) => {
  switch (validationError.id) {
    case 'empty':
      return i18n.t('volcanes:validation-error.fieldCardExpiryDateMessageErrors.emptyMessage');

    case 'expired':
      return i18n.t('volcanes:validation-error.fieldCardExpiryDateMessageErrors.expiredMessage');

    default:
      return i18n.t('volcanes:validation-error.fieldCardExpiryDateMessageErrors.defaultMessage');
  }
};

function ValidationMessage({ validationError }: ValidationMessageProps): Node {
  return (
    <ErrorLabel>
      {messages(validationError) || (serverErrorMessage(validationError.payload))}
    </ErrorLabel>
  );
}

const EXPIRY_DATE_MASK = [
  /[0-9]/, /[0-9]/,
  '/',
  /[0-9]/, /[0-9]/, /[0-9]/, /[0-9]/,
];
